// ===========================================
// StatWidget Component
// ===========================================
// Reusable stat card with consistent brand styling
// Used across Admin, Teacher, and Parent dashboards

import React from 'react';
import { Card, CardContent, Typography, Box, Skeleton, CardActionArea } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { TrendingUp, TrendingDown, TrendingFlat } from '@mui/icons-material';

// ===========================================
// TYPES
// ===========================================

export interface StatWidgetProps {
  title: string;
  value: string | number;
  icon: React.ReactNode;
  color?: 'primary' | 'secondary' | 'success' | 'warning' | 'error' | 'info' | 'mint' | 'coral' | 'yellow';
  subtitle?: string;
  trend?: {
    value: number;
    direction: 'up' | 'down' | 'neutral';
  };
  loading?: boolean;
  onClick?: () => void;
  href?: string;
  /** Compact mode shows a smaller, condensed version */
  compact?: boolean;
  /** Show a highlight/pulse animation to draw attention (e.g., new items) */
  highlight?: boolean;
  /** In compact mode, stack the label below the value instead of beside it */
  stackLabel?: boolean;
}

// Color mappings for icon backgrounds (using brand colors)
const colorBackgrounds: Record<string, { bg: string; icon: string }> = {
  primary: { bg: '#a3d9f6', icon: '#4580E4' },   // Blue
  secondary: { bg: '#FFE066', icon: '#E6B800' }, // Yellow
  success: { bg: '#c5ebe2', icon: '#5cb399' },   // Mint/Teal
  warning: { bg: '#ffd4cc', icon: '#e67761' },   // Coral/Pink
  error: { bg: '#ffcccc', icon: '#ff4040' },     // Red
  info: { bg: '#E8D4F0', icon: '#9B59B6' },      // Purple (distinct from primary)
  mint: { bg: '#c5ebe2', icon: '#5cb399' },      // Mint/Teal (same as success)
  coral: { bg: '#ffd4cc', icon: '#e67761' },     // Coral/Pink (same as warning)
  yellow: { bg: '#FFF3CC', icon: '#FFCE00' },    // Brand Yellow
};

// ===========================================
// COMPONENT
// ===========================================

export function StatWidget({
  title,
  value,
  icon,
  color = 'primary',
  subtitle,
  trend,
  loading = false,
  onClick,
  href,
  compact = false,
  highlight = false,
  stackLabel = false,
}: StatWidgetProps) {
  const navigate = useNavigate();
  const colors = colorBackgrounds[color] || colorBackgrounds.primary;

  // Highlight styling for when there are new/pending items
  const highlightSx = highlight
    ? {
        border: 2,
        borderColor: 'warning.main',
        animation: 'pulse 2s ease-in-out infinite',
        '@keyframes pulse': {
          '0%, 100%': {
            boxShadow: '0 0 0 0 rgba(255, 152, 0, 0.4)',
          },
          '50%': {
            boxShadow: '0 0 0 8px rgba(255, 152, 0, 0)',
          },
        },
      }
    : {};

  const handleClick = () => {
    if (href) {
      navigate(href);
    } else if (onClick) {
      onClick();
    }
  };

  const isClickable = !!href || !!onClick;

  // Compact layout - horizontal with minimal padding
  if (compact) {
    const compactContent = (
      <CardContent sx={{ p: 1.5, '&:last-child': { pb: 1.5 } }}>
        <Box sx={{ display: 'flex', alignItems: stackLabel ? 'flex-start' : 'center', gap: 1.5 }}>
          {/* Icon */}
          <Box
            sx={{
              bgcolor: colors.bg,
              borderRadius: '50%',
              width: 32,
              height: 32,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              color: colors.icon,
              flexShrink: 0,
              '& .MuiSvgIcon-root': {
                fontSize: 16,
              },
            }}
          >
            {icon}
          </Box>
          {/* Value and Title */}
          <Box sx={{ minWidth: 0, flex: 1 }}>
            {loading ? (
              <Skeleton variant="text" width={40} height={28} />
            ) : (
              <Typography
                variant="h6"
                component="div"
                sx={{
                  fontWeight: 700,
                  color: 'text.primary',
                  lineHeight: 1,
                }}
              >
                {value}
              </Typography>
            )}
            {/* Title - stacked below value when stackLabel is true */}
            {stackLabel && (
              <Typography
                color="text.secondary"
                variant="caption"
                sx={{ fontWeight: 500, display: 'block', mt: 0.5 }}
              >
                {title}
              </Typography>
            )}
          </Box>
          {/* Title - inline when stackLabel is false */}
          {!stackLabel && (
            <Typography
              color="text.secondary"
              variant="caption"
              sx={{ fontWeight: 500, whiteSpace: 'nowrap' }}
            >
              {title}
            </Typography>
          )}
        </Box>
      </CardContent>
    );

    if (isClickable) {
      return (
        <Card sx={{ height: '100%', ...highlightSx }}>
          <CardActionArea onClick={handleClick} sx={{ height: '100%' }}>
            {compactContent}
          </CardActionArea>
        </Card>
      );
    }

    return <Card sx={{ height: '100%', ...highlightSx }}>{compactContent}</Card>;
  }

  // Normal layout - vertical with full details
  const cardContent = (
    <CardContent sx={{ p: 2.5, '&:last-child': { pb: 2.5 } }}>
      {/* Title row with icon on the left - fixed height for alignment */}
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 1, minHeight: 40 }}>
        <Box
          sx={{
            bgcolor: colors.bg,
            borderRadius: '50%',
            width: 40,
            height: 40,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            color: colors.icon,
            mr: 1.5,
            flexShrink: 0,
            '& .MuiSvgIcon-root': {
              fontSize: 20,
            },
          }}
        >
          {icon}
        </Box>
        <Typography
          color="text.secondary"
          variant="body2"
          sx={{ fontWeight: 500, lineHeight: 1.3 }}
        >
          {title}
        </Typography>
      </Box>

      {/* Value - fixed height for alignment across cards */}
      <Box sx={{ minHeight: 48, display: 'flex', alignItems: 'center' }}>
        {loading ? (
          <Skeleton variant="text" width={80} height={48} />
        ) : (
          <Typography
            variant="h4"
            component="div"
            sx={{
              fontWeight: 700,
              color: 'text.primary',
              lineHeight: 1.2,
            }}
          >
            {value}
          </Typography>
        )}
      </Box>

      {/* Subtitle or Trend - fixed height for alignment */}
      <Box sx={{ minHeight: 20 }}>
        {(subtitle || trend) && !loading && (
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            {trend && (
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  mr: 1,
                  color:
                    trend.direction === 'up'
                      ? 'success.main'
                      : trend.direction === 'down'
                        ? 'error.main'
                        : 'text.secondary',
                }}
              >
                {trend.direction === 'up' && <TrendingUp fontSize="small" />}
                {trend.direction === 'down' && <TrendingDown fontSize="small" />}
                {trend.direction === 'neutral' && <TrendingFlat fontSize="small" />}
                <Typography variant="caption" sx={{ ml: 0.25, fontWeight: 600 }}>
                  {trend.value > 0 ? '+' : ''}
                  {trend.value}%
                </Typography>
              </Box>
            )}
            {subtitle && (
              <Typography variant="caption" color="text.secondary">
                {subtitle}
              </Typography>
            )}
          </Box>
        )}

        {loading && subtitle && <Skeleton variant="text" width={60} height={20} />}
      </Box>
    </CardContent>
  );

  if (isClickable) {
    return (
      <Card sx={{ height: '100%', ...highlightSx }}>
        <CardActionArea onClick={handleClick} sx={{ height: '100%' }}>
          {cardContent}
        </CardActionArea>
      </Card>
    );
  }

  return <Card sx={{ height: '100%', ...highlightSx }}>{cardContent}</Card>;
}

export default StatWidget;
